import { useQuery } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { dashboardConfigApi } from '@/lib/api'
import { StatWidget, BaseWidget } from './BaseWidget'
import { DollarSign, Users, FileText, Receipt } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { DashboardStats, InvoiceStatusCount, MonthlyRevenue, MonthlyExpense } from '@/types'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, CartesianGrid } from 'recharts'

// Hook to get container dimensions using a callback ref
function useContainerDimensions() {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null)
  const [element, setElement] = useState<HTMLDivElement | null>(null)

  // Callback ref that will be called when the element is mounted/unmounted
  const containerRef = useRef<HTMLDivElement | null>(null)
  const setRef = (node: HTMLDivElement | null) => {
    containerRef.current = node
    setElement(node)
  }

  useEffect(() => {
    if (!element) return

    let rafId: number
    let timeoutId: ReturnType<typeof setTimeout>

    const checkDimensions = () => {
      if (element) {
        const { offsetWidth, offsetHeight } = element
        if (offsetWidth > 0 && offsetHeight > 0) {
          setDimensions({ width: offsetWidth, height: offsetHeight })
        } else {
          timeoutId = setTimeout(() => {
            rafId = requestAnimationFrame(checkDimensions)
          }, 50)
        }
      }
    }

    // Check dimensions immediately
    checkDimensions()

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setDimensions({ width: Math.floor(width), height: Math.floor(height) })
        }
      }
    })

    resizeObserver.observe(element)

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [element])

  return { containerRef: setRef, dimensions }
}

// Total Revenue Widget
export function TotalRevenueWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'TOTAL_REVENUE'],
    queryFn: () => dashboardConfigApi.getWidgetData<DashboardStats>('TOTAL_REVENUE'),
  })

  const stats = data?.data
  const currency = stats?.currency || 'USD'

  // Calculate percentage change (mock for now, could be from API)
  const percentChange = 12.4

  return (
    <StatWidget
      title={t('dashboard.totalRevenue')}
      value={formatCurrency(stats?.total_revenue || 0, currency)}
      icon={<DollarSign className="h-5 w-5" />}
      iconBgColor="#7BBDE8"
      trend={{ value: percentChange, isPositive: percentChange >= 0 }}
      isLoading={isLoading}
    />
  )
}

// Outstanding Widget
export function OutstandingWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'OUTSTANDING'],
    queryFn: () => dashboardConfigApi.getWidgetData<DashboardStats>('OUTSTANDING'),
  })

  const stats = data?.data
  const currency = stats?.currency || 'USD'
  const overdueAmount = stats?.overdue_amount || 0

  return (
    <StatWidget
      title={t('dashboard.outstanding')}
      value={formatCurrency(stats?.outstanding_amount || 0, currency)}
      icon={<FileText className="h-5 w-5" />}
      iconBgColor="#FF2F6D"
      badge={overdueAmount > 0 ? (
        <span className="text-xs font-medium text-[#FF2F6D]">
          {formatCurrency(overdueAmount, currency)} {t('dashboard.overdue')}
        </span>
      ) : undefined}
      isLoading={isLoading}
    />
  )
}

// Total Clients Widget
export function TotalClientsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'TOTAL_CLIENTS'],
    queryFn: () => dashboardConfigApi.getWidgetData<DashboardStats>('TOTAL_CLIENTS'),
  })

  const stats = data?.data
  return (
    <StatWidget
      title={t('dashboard.totalClients')}
      value={stats?.total_clients || 0}
      subtitle={`+${stats?.new_clients_this_month || 0} ${t('common.thisMonth')}`}
      icon={<Users className="h-5 w-5" />}
      iconBgColor="#438CC4"
      isLoading={isLoading}
    />
  )
}

// Total Invoices Widget
export function TotalInvoicesWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'TOTAL_INVOICES'],
    queryFn: () => dashboardConfigApi.getWidgetData<DashboardStats>('TOTAL_INVOICES'),
  })

  const stats = data?.data
  return (
    <StatWidget
      title={t('dashboard.totalInvoices')}
      value={stats?.total_invoices || 0}
      subtitle={`+${stats?.invoices_this_month || 0} ${t('common.thisMonth')}`}
      icon={<FileText className="h-5 w-5" />}
      iconBgColor="#314A7C"
      isLoading={isLoading}
    />
  )
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(entry.value)}
          </p>
        ))}
      </div>
    )
  }
  return null
}

// Revenue vs Expenses Chart Widget
export function RevenueVsExpensesWidget() {
  const { t } = useTranslation()
  const { containerRef, dimensions } = useContainerDimensions()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'REVENUE_VS_EXPENSES'],
    queryFn: () => dashboardConfigApi.getWidgetData<{ revenue: MonthlyRevenue[]; expenses: MonthlyExpense[] }>('REVENUE_VS_EXPENSES'),
  })

  // Build chart data from either revenue or expenses (whichever has data)
  const revenue = data?.data?.revenue || []
  const expenses = data?.data?.expenses || []

  // Create a map of all months from both datasets
  const monthsMap = new Map<string, { month: string; revenue: number; expenses: number }>()

  revenue.forEach((r) => {
    const key = `${r.year}-${r.month}`
    monthsMap.set(key, { month: r.month, revenue: r.revenue, expenses: 0 })
  })

  expenses.forEach((e) => {
    const key = `${e.year}-${e.month}`
    const existing = monthsMap.get(key)
    if (existing) {
      existing.expenses = e.amount
    } else {
      monthsMap.set(key, { month: e.month, revenue: 0, expenses: e.amount })
    }
  })

  const chartData = Array.from(monthsMap.values())

  const hasData = chartData.length > 0

  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  return (
    <BaseWidget title={t('dashboard.revenueVsExpenses')} isLoading={isLoading}>
      <div ref={containerRef} className="w-full h-[200px]">
        {hasData && dimensions ? (
          <BarChart data={chartData} width={dimensions.width} height={dimensions.height} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="month"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              tick={{ fill: '#6b7280' }}
            />
            <YAxis
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#6b7280' }}
              tickFormatter={formatYAxis}
              width={45}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
            <Legend
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
            />
            <Bar
              dataKey="revenue"
              fill="#22c55e"
              name={t('dashboard.revenue')}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
            <Bar
              dataKey="expenses"
              fill="#ef4444"
              name={t('dashboard.expenses')}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        ) : !hasData && !isLoading ? (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
            {t('common.noData')}
          </div>
        ) : null}
      </div>
    </BaseWidget>
  )
}

// Invoice Status Widget
const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#94a3b8',
  SENT: '#3b82f6',
  PAID: '#22c55e',
  OVERDUE: '#ef4444',
  CANCELLED: '#6b7280',
  PARTIALLY_PAID: '#f59e0b',
}

// Custom tooltip for pie chart
const PieTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { color: string } }> }) => {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.payload.color }} />
          <span className="text-sm font-medium">{data.name}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Count: <span className="font-medium text-foreground">{data.value}</span>
        </p>
      </div>
    )
  }
  return null
}

export function InvoiceStatusWidget() {
  const { t } = useTranslation()
  const { containerRef, dimensions } = useContainerDimensions()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'INVOICE_STATUS'],
    queryFn: () => dashboardConfigApi.getWidgetData<InvoiceStatusCount[]>('INVOICE_STATUS'),
  })

  const chartData = data?.data?.filter(s => s.status).map(s => ({
    name: t(`billing.${s.status.toLowerCase()}`),
    value: s.count,
    color: STATUS_COLORS[s.status] || '#94a3b8',
  })) || []

  const total = chartData.reduce((sum, item) => sum + item.value, 0)
  const hasData = chartData.length > 0 && total > 0

  return (
    <BaseWidget title={t('dashboard.invoicesByStatus')} isLoading={isLoading}>
      <div className="flex items-center gap-4">
        <div ref={containerRef} className="w-[140px] h-[140px] flex-shrink-0">
          {hasData && dimensions ? (
            <PieChart width={dimensions.width} height={dimensions.height}>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={55}
                paddingAngle={2}
                strokeWidth={0}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          ) : !hasData && !isLoading ? (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              {t('common.noData')}
            </div>
          ) : null}
        </div>
        <div className="flex-1 space-y-2">
          {chartData.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-muted-foreground">{item.name}</span>
              </div>
              <span className="font-medium">{item.value}</span>
            </div>
          ))}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground font-medium">{t('common.total')}</span>
              <span className="font-bold">{total}</span>
            </div>
          )}
        </div>
      </div>
    </BaseWidget>
  )
}

// Expenses Overview Widget
export function ExpensesOverviewWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'EXPENSES_OVERVIEW'],
    queryFn: () => dashboardConfigApi.getWidgetData<DashboardStats>('EXPENSES_OVERVIEW'),
  })

  const stats = data?.data
  const currency = stats?.currency || 'USD'
  return (
    <BaseWidget title={t('dashboard.expensesOverview')} icon={<Receipt className="h-4 w-4 text-muted-foreground" />} isLoading={isLoading}>
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">{t('common.thisMonth')}</p>
          <p className="text-xl font-bold">{formatCurrency(stats?.expenses_this_month || 0, currency)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{t('common.thisYear')}</p>
          <p className="text-xl font-bold">{formatCurrency(stats?.expenses_this_year || 0, currency)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{t('dashboard.monthlyProfit')}</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(stats?.monthly_profit || 0, currency)}</p>
        </div>
      </div>
    </BaseWidget>
  )
}

